import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ADMIN_PROFILE_URL, PROFILE_URL } from '../constants';
import {
  ApiUuid,
  BulkContactAddressResponse,
  BulkContactDegreeResponse,
  BulkContactEmailResponse,
  BulkContactPhoneResponse,
  BulkContactSpecialtyResponse,
  BulkCreateContactAddressesRequest,
  BulkCreateContactDegreesRequest,
  BulkCreateContactEmailsRequest,
  BulkCreateContactPhonesRequest,
  BulkCreateContactSpecialtiesRequest,
  ContactDetailResponse,
  Profile,
  UpdateContactRequest,
} from '../types';
import { UpdateUserResponse } from '@data-access/user-management/types';

@Injectable()
export class ProfileService {
  private readonly _httpClient = inject(HttpClient);

  /*
   * @NOTE: - MY PROFILE PAGE END POINTS
   * */
  getProfile() {
    return this._httpClient.get<Profile>(PROFILE_URL);
  }

  updateProfileContact(payload: UpdateContactRequest) {
    return this._httpClient.post<ContactDetailResponse>(
      `${PROFILE_URL}contact/`,
      payload,
    );
  }

  updateProfileAddresses(addressPayload: BulkCreateContactAddressesRequest) {
    return this._httpClient.post<BulkContactAddressResponse>(
      `${PROFILE_URL}addresses/`,
      addressPayload,
    );
  }

  updateProfilePhoneNumbers(phonePayload: BulkCreateContactPhonesRequest) {
    return this._httpClient.post<BulkContactPhoneResponse>(
      `${PROFILE_URL}phones/`,
      phonePayload,
    );
  }

  updateProfileEmails(emailPayload: BulkCreateContactEmailsRequest) {
    return this._httpClient.post<BulkContactEmailResponse>(
      `${PROFILE_URL}emails/`,
      emailPayload,
    );
  }

  updateProfileSpecialties(
    specialtyPayload: BulkCreateContactSpecialtiesRequest,
  ) {
    return this._httpClient.post<BulkContactSpecialtyResponse>(
      `${PROFILE_URL}specialties/`,
      specialtyPayload,
    );
  }

  updateProfileDegrees(degreePayload: BulkCreateContactDegreesRequest) {
    return this._httpClient.post<BulkContactDegreeResponse>(
      `${PROFILE_URL}degrees/`,
      degreePayload,
    );
  }

  /*
   *
   * @NOTE: - PEOPLE PAGE PROFILE END POINTS
   *
   * */
  getAdminProfile(userId: ApiUuid) {
    return this._httpClient.get<Profile>(this._adminUserPath(userId));
  }

  updateAdminProfileContact(userId: ApiUuid, payload: UpdateContactRequest) {
    return this._httpClient.post<ContactDetailResponse>(
      `${this._adminUserPath(userId)}contact/`,
      payload,
    );
  }

  updateAdminProfileAddresses(
    userId: ApiUuid,
    addressPayload: BulkCreateContactAddressesRequest,
  ) {
    return this._httpClient.post<BulkContactAddressResponse>(
      `${this._adminUserPath(userId)}addresses/`,
      {
        addresses: addressPayload.addresses,
      },
    );
  }

  updateAdminProfilePhoneNumbers(
    userId: ApiUuid,
    phonePayload: BulkCreateContactPhonesRequest,
  ) {
    return this._httpClient.post<BulkContactPhoneResponse>(
      `${this._adminUserPath(userId)}phones/`,
      phonePayload,
    );
  }

  updateAdminProfileEmails(
    userId: ApiUuid,
    emailPayload: BulkCreateContactEmailsRequest,
  ) {
    return this._httpClient.post<BulkContactEmailResponse>(
      `${this._adminUserPath(userId)}emails/`,
      emailPayload,
    );
  }

  updateAdminProfileSpecialties(
    userId: ApiUuid,
    specialtyPayload: BulkCreateContactSpecialtiesRequest,
  ) {
    return this._httpClient.post<BulkContactSpecialtyResponse>(
      `${this._adminUserPath(userId)}specialties/`,
      specialtyPayload,
    );
  }

  updateAdminProfileDegrees(
    userId: ApiUuid,
    degreePayload: BulkCreateContactDegreesRequest,
  ) {
    return this._httpClient.post<BulkContactDegreeResponse>(
      `${this._adminUserPath(userId)}degrees/`,
      degreePayload,
    );
  }

  updateAdminProfileUserAttribute(
    userId: ApiUuid,
    payload: { attribute: string },
  ) {
    return this._httpClient.put<UpdateUserResponse>(
      `${this._adminUserPath(userId)}smeqc/`,
      payload,
    );
  }

  updateAdminProfileUserRole(userId: ApiUuid, payload: { role_id: ApiUuid }) {
    return this._httpClient.put<UpdateUserResponse>(
      `${this._adminUserPath(userId)}role/`,
      payload,
    );
  }

  updateAdminProfileUserStatus(userId: ApiUuid, payload: { status: string }) {
    return this._httpClient.put<UpdateUserResponse>(
      `${this._adminUserPath(userId)}status/`,
      payload,
    );
  }

  private _adminUserPath(userId: ApiUuid) {
    return `${ADMIN_PROFILE_URL}${userId}/`;
  }
}
